import { parsePathQueryString } from '@/shared/helpers/parsePathQueryString'

interface ParentPageState {
  query: Record<string, string>
  timestamp: number
}

/**
 * Service to manage parent page state across navigation
 * This ensures filters are preserved when navigating to child pages and back
 */
class ParentPageStateService {
  private readonly STORAGE_KEY = 'arbela_parent_page_states'
  private readonly STATE_EXPIRY = 30 * 60 * 1000 // 30 minutes

  /**
   * Store the current page state when navigating to a child page
   * @param parentRouteName The name of the parent route
   * @param currentQuery Current query parameters to store
   */
  storeParentPageState(
    parentRouteName: string,
    currentQuery?: Record<string, string>
  ): void {
    if (!parentRouteName) return

    const query = currentQuery || parsePathQueryString(location.search)

    // Only store if there are meaningful query parameters (filters, search, etc.)
    if (this.hasSignificantQuery(query)) {
      const states = this.getStoredStates()
      states[parentRouteName] = {
        query,
        timestamp: Date.now()
      }

      // Clean expired states
      this.cleanExpiredStates(states)

      sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(states))
    }
  }

  /**
   * Retrieve stored state for a parent page
   * @param parentRouteName The name of the parent route
   * @returns The stored query parameters or null if not found/expired
   */
  getParentPageState(parentRouteName: string): Record<string, string> | null {
    if (!parentRouteName) return null

    const states = this.getStoredStates()
    const state = states[parentRouteName]

    if (!state) return null

    // Check if state is expired
    if (Date.now() - state.timestamp > this.STATE_EXPIRY) {
      delete states[parentRouteName]
      sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(states))
      return null
    }

    return state.query
  }

  /**
   * Clear stored state for a specific parent page
   * @param parentRouteName The name of the parent route
   */
  clearParentPageState(parentRouteName: string): void {
    if (!parentRouteName) return

    const states = this.getStoredStates()
    delete states[parentRouteName]
    sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(states))
  }

  /**
   * Clear all stored states
   */
  clearAllStates(): void {
    sessionStorage.removeItem(this.STORAGE_KEY)
  }

  /**
   * Get all stored states from sessionStorage
   */
  private getStoredStates(): Record<string, ParentPageState> {
    try {
      const stored = sessionStorage.getItem(this.STORAGE_KEY)
      return stored ? JSON.parse(stored) : {}
    } catch {
      return {}
    }
  }

  /**
   * Check if query parameters contain significant data worth storing
   * @param query Query parameters to check
   */
  private hasSignificantQuery(query: Record<string, string>): boolean {
    const significantKeys = [
      'filter',
      'search',
      'sortby',
      'sortdesc',
      'page',
      'itemsperpage'
    ]
    const dateIdKeys = Object.keys(query).filter(key =>
      key.startsWith('dateId')
    )

    return (
      significantKeys.some(
        key => query[key] && query[key] !== '{}' && query[key] !== ''
      ) || dateIdKeys.length > 0
    )
  }

  /**
   * Remove expired states from storage
   * @param states Current states object
   */
  private cleanExpiredStates(states: Record<string, ParentPageState>): void {
    const now = Date.now()
    Object.keys(states).forEach(key => {
      if (now - states[key].timestamp > this.STATE_EXPIRY) {
        delete states[key]
      }
    })
  }
}

export const parentPageStateService = new ParentPageStateService()
